import React, { useState } from "react";
import axiosClient from "../../../api/axiosClient";
import "./UserFormModal.css";

const ROLE_OPTIONS = [
  { value: 0, label: "Admin" },
  { value: 2, label: "Manager" },
  { value: 3, label: "Staff" },
];

export default function UserFormModal({ user, endpoint, onClose, onSaved }) {
  const isEdit = Boolean(user);

  const [username, setUsername] = useState(user?.employeeName || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user?.role ?? 3);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photo || null);

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!username.trim() || !email.trim() || !phone.trim() || (!isEdit && !password)) {
      setError("Please fill in all required fields.");
      return;
    }

    const form = new FormData();
    form.append("username", username.trim());
    form.append("email", email.trim());
    form.append("phone", phone.trim());
    form.append("role", role);
    if (password) form.append("password", password);
    if (photoFile) form.append("profile_photo", photoFile);

    setSaving(true);
    try {
      if (isEdit) {
        await axiosClient.put(`${endpoint}${user.id}/`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onSaved("User updated.");
      } else {
        await axiosClient.post(endpoint, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onSaved("User created.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ufm__overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ufm__card" role="dialog" aria-modal="true">
        <button className="ufm__close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 className="ufm__title">{isEdit ? "Edit user" : "Add user"}</h2>
        <p className="ufm__sub">
          {isEdit ? "Update this team member's details." : "Create a login for a new team member."}
        </p>

        <form onSubmit={handleSubmit} className="ufm__form">
          <div className="ufm__photo-row">
            <label className="ufm__photo" htmlFor="ufm-photo">
              {photoPreview ? (
                <img src={photoPreview} alt="" />
              ) : (
                <span>{(username || "?").charAt(0).toUpperCase()}</span>
              )}
              <input id="ufm-photo" type="file" accept="image/*" onChange={handlePhotoChange} hidden />
            </label>
            <div className="ufm__photo-hint">
              <strong>Profile photo</strong>
              <span>Click the circle to upload or replace it.</span>
            </div>
          </div>

          <div className="ufm__grid">
            <label className="ufm__field">
              <span>Username *</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} disabled={saving} />
            </label>

            <label className="ufm__field">
              <span>Email *</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving} />
            </label>

            <label className="ufm__field">
              <span>Phone *</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={saving} />
            </label>

            <label className="ufm__field">
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(Number(e.target.value))} disabled={saving}>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="ufm__field ufm__field--full">
              <span>{isEdit ? "New password (leave blank to keep current)" : "Password *"}</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={saving} />
            </label>
          </div>

          {error && <div className="ufm__error">{error}</div>}

          <div className="ufm__actions">
            <button type="button" className="ufm__btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="ufm__btn-primary" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
